import React, { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import {
  Briefcase,
  Users,
  MessageSquare,
  Rocket,
  CheckCircle2,
  TrendingUp,
  Clock,
} from "lucide-react";

const pipeline = [
  { icon: Briefcase, label: "Post project", desc: "Scope & milestones" },
  { icon: Users, label: "Match talent", desc: "Vetted developers" },
  { icon: MessageSquare, label: "Collaborate", desc: "Tasks & chat" },
  { icon: Rocket, label: "Ship", desc: "Deploy & pay" },
];

const liveProjects = [
  {
    name: "E-commerce rebuild",
    status: "In progress",
    progress: 72,
    team: "3 devs",
    accent: "from-blue-500 to-cyan-500",
  },
  {
    name: "Mobile banking MVP",
    status: "Review",
    progress: 94,
    team: "2 devs",
    accent: "from-violet-500 to-purple-500",
  },
];

const PlatformShowcase: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const stepTimer = setInterval(() => {
      setActiveStep((s) => (s + 1) % pipeline.length);
      setProgress(0);
    }, 3200);
    return () => clearInterval(stepTimer);
  }, []);

  useEffect(() => {
    const barTimer = setInterval(() => {
      setProgress((p) => (p >= 100 ? 100 : p + 4));
    }, 80);
    return () => clearInterval(barTimer);
  }, [activeStep]);

  return (
    <div className="relative w-full max-w-lg mx-auto lg:mx-0 lg:ml-auto">
      {/* Ambient glow */}
      <div
        className={`absolute -inset-4 rounded-3xl blur-3xl opacity-40 ${
          isDark
            ? "bg-gradient-to-br from-blue-600/30 via-cyan-500/20 to-violet-600/30"
            : "bg-gradient-to-br from-blue-400/25 via-cyan-300/20 to-violet-400/25"
        }`}
        aria-hidden
      />

      <div
        className={`relative rounded-2xl border overflow-hidden shadow-2xl backdrop-blur-xl ${
          isDark
            ? "bg-gray-900/80 border-gray-700/80"
            : "bg-white/90 border-gray-200/80"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-5 py-4 border-b ${
            isDark ? "border-gray-800" : "border-gray-100"
          }`}
        >
          <div>
            <p
              className={`text-xs font-semibold uppercase tracking-wider ${
                isDark ? "text-cyan-400" : "text-blue-600"
              }`}
            >
              Live workspace
            </p>
            <p
              className={`text-sm font-medium mt-0.5 ${
                isDark ? "text-gray-200" : "text-gray-800"
              }`}
            >
              AetherFlow Platform
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              isDark
                ? "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30"
                : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
            }`}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Active
          </span>
        </div>

        <div className="p-5 space-y-5">
          {/* Pipeline */}
          <div>
            <p
              className={`text-xs mb-3 ${
                isDark ? "text-gray-500" : "text-gray-500"
              }`}
            >
              Delivery pipeline
            </p>
            <div className="grid grid-cols-4 gap-2">
              {pipeline.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === activeStep;
                const isDone = index < activeStep;
                return (
                  <div
                    key={step.label}
                    className={`relative flex flex-col items-center text-center p-2 rounded-xl transition-all duration-500 ${
                      isActive
                        ? isDark
                          ? "bg-blue-500/15 ring-1 ring-blue-400/40 scale-105"
                          : "bg-blue-50 ring-1 ring-blue-200 scale-105"
                        : isDone
                        ? isDark
                          ? "bg-gray-800/60"
                          : "bg-gray-50"
                        : "opacity-60"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${
                        isActive || isDone
                          ? "bg-gradient-to-br from-blue-500 to-cyan-500 text-white"
                          : isDark
                          ? "bg-gray-800 text-gray-500"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {isDone ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        <Icon className="w-4 h-4" />
                      )}
                    </div>
                    <span
                      className={`text-[10px] font-semibold leading-tight ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
            <div
              className={`mt-3 h-1.5 rounded-full overflow-hidden ${
                isDark ? "bg-gray-800" : "bg-gray-100"
              }`}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-violet-500 transition-all duration-100 ease-linear"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>

          {/* Project cards */}
          <div className="space-y-3">
            {liveProjects.map((project, i) => (
              <div
                key={project.name}
                className={`rounded-xl p-4 transition-all duration-500 ${
                  isDark
                    ? "bg-gray-800/50 border border-gray-700/50"
                    : "bg-gray-50 border border-gray-100"
                } ${i === activeStep % 2 ? "translate-x-0 opacity-100" : "opacity-90"}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p
                      className={`text-sm font-semibold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {project.name}
                    </p>
                    <p
                      className={`text-xs mt-0.5 ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {project.team}
                    </p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      isDark
                        ? "bg-blue-500/15 text-blue-300"
                        : "bg-blue-50 text-blue-700"
                    }`}
                  >
                    {project.status}
                  </span>
                </div>
                <div
                  className={`h-1.5 rounded-full overflow-hidden ${
                    isDark ? "bg-gray-700" : "bg-gray-200"
                  }`}
                >
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${project.accent} transition-all duration-700`}
                    style={{
                      width: `${project.progress + (activeStep === i ? 2 : 0)}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            {[
              { icon: TrendingUp, value: "500+", label: "Developers" },
              { icon: Briefcase, value: "1.2k", label: "Projects" },
              { icon: Clock, value: "98%", label: "On-time" },
            ].map((metric) => {
              const Icon = metric.icon;
              return (
                <div
                  key={metric.label}
                  className={`rounded-xl p-3 text-center ${
                    isDark ? "bg-gray-800/40" : "bg-white border border-gray-100"
                  }`}
                >
                  <Icon
                    className={`w-4 h-4 mx-auto mb-1 ${
                      isDark ? "text-cyan-400" : "text-blue-600"
                    }`}
                  />
                  <p
                    className={`text-sm font-bold ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {metric.value}
                  </p>
                  <p
                    className={`text-[10px] ${
                      isDark ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    {metric.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-center gap-1.5 pb-4">
          {pipeline.map((_, i) => (
            <span
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === activeStep ? "w-4 h-1.5" : "w-1.5 h-1.5"
              } ${
                i === activeStep
                  ? "bg-gradient-to-r from-blue-500 to-cyan-500"
                  : isDark
                  ? "bg-gray-600"
                  : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformShowcase;
